var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import axios from "axios";
import Vue, { computed, ref, watch } from "vue";
import { GalaxyApi } from "@/api";
import { fetchCollectionSummary } from "@/api/datasetCollections";
import { useToast } from "@/composables/toast";
import { useDatasetStore } from "@/stores/datasetStore";
import { useHistoryStore } from "@/stores/historyStore";
import { useWorkflowStore } from "@/stores/workflowStore";
import _l from "@/utils/localization";
import { withPrefix } from "@/utils/redirect";
import { errorMessageAsString } from "@/utils/simple-error";
import { initializeObjectReferences, initializeObjectToHistoryRefs, updateReferences, } from "./object-permission-composables";
import PermissionObjectType from "./PermissionObjectType.vue";
import SharingIndicator from "./SharingIndicator.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'ObjectPermissions',
    props: {
        markdownContent: null
    },
    setup(__props) {
        const props = __props;
        const { getHistoryNameById, loadHistoryById } = useHistoryStore();
        const { getStoredWorkflowNameByInstanceId, fetchWorkflowForInstanceId } = useWorkflowStore();
        const { getDataset, fetchDataset } = useDatasetStore();
        const toast = useToast();
        const referencedObjects = initializeObjectReferences();
        const { referencedJobIds, referencedHistoryDatasetIds, referencedHistoryDatasetCollectionIds, referencedWorkflowIds, referencedInvocationIds, } = referencedObjects;
        // We mostly defer to history permissions for various objects. Track them and merge
        // into histories IDs.
        const { jobsToHistories, invocationsToHistories, historyDatasetCollectionsToHistories, historyIds } = initializeObjectToHistoryRefs(referencedObjects);
        const historyAccessible = ref({});
        const workflowAccessible = ref({});
        const historyDatasetAccessible = ref({});
        function catchErrorToToast(title, prolog) {
            function handleError(e) {
                toast.error(`${prolog} Reason: ${errorMessageAsString(e)}.`, title);
            }
            return handleError;
        }
        watch(referencedJobIds, () => __awaiter(this, void 0, void 0, function* () {
            referencedJobIds.value.forEach((jobId) => __awaiter(this, void 0, void 0, function* () {
                if (jobId in jobsToHistories.value) {
                    return;
                }
                const handleError = catchErrorToToast("Failed to job information", "Some referenced objects may not be listed.");
                const { data, error } = yield GalaxyApi().GET("/api/jobs/{job_id}", {
                    params: { path: { job_id: jobId } },
                });
                if (error) {
                    handleError(error);
                    return;
                }
                if ("history_id" in data) {
                    const historyId = data.history_id;
                    Vue.set(jobsToHistories.value, jobId, historyId);
                }
            }));
        }));
        watch(referencedInvocationIds, () => __awaiter(this, void 0, void 0, function* () {
            referencedInvocationIds.value.forEach((invocationId) => __awaiter(this, void 0, void 0, function* () {
                if (invocationId in invocationsToHistories.value) {
                    return;
                }
                const handleError = catchErrorToToast("Failed to fetch workflow information", "Some referenced objects may not be listed.");
                const { data, error } = yield GalaxyApi().GET("/api/invocations/{invocation_id}", {
                    params: { path: { invocation_id: invocationId } },
                });
                if (error) {
                    handleError(error);
                    return;
                }
                if ("history_id" in data) {
                    const historyId = data.history_id;
                    Vue.set(invocationsToHistories.value, invocationId, historyId);
                }
            }));
        }));
        watch(referencedHistoryDatasetCollectionIds, () => __awaiter(this, void 0, void 0, function* () {
            referencedHistoryDatasetCollectionIds.value.forEach((historyDatasetCollectionId) => {
                if (historyDatasetCollectionId in historyDatasetCollectionsToHistories.value) {
                    return;
                }
                const handleError = catchErrorToToast("Failed to fetch collection information", "Some referenced objects may not be listed.");
                fetchCollectionSummary({ id: historyDatasetCollectionId })
                    .then((data) => {
                    const historyId = data.history_id;
                    Vue.set(historyDatasetCollectionsToHistories.value, historyDatasetCollectionId, historyId);
                })
                    .catch(handleError);
            });
        }));
        const histories = computed(() => {
            return historyIds.value.map((historyId) => {
                return {
                    id: historyId,
                    type: "history",
                    name: getHistoryNameById(historyId),
                    accessible: historyAccessible.value[historyId],
                };
            });
        });
        const workflows = computed(() => {
            return referencedWorkflowIds.value.map((workflowId) => {
                return {
                    id: workflowId,
                    type: "workflow",
                    name: getStoredWorkflowNameByInstanceId(workflowId),
                    accessible: workflowAccessible.value[workflowId],
                };
            });
        });
        const datasets = computed(() => {
            return referencedHistoryDatasetIds.value.map((historyDatasetId) => {
                var _a;
                return {
                    id: historyDatasetId,
                    type: "historyDataset",
                    name: ((_a = getDataset(historyDatasetId)) === null || _a === void 0 ? void 0 : _a.name) || "Fetching dataset name...",
                    accessible: historyDatasetAccessible.value[historyDatasetId],
                };
            });
        });
        const loading = ref(false);
        const SHARING_FIELD = { key: "accessible", label: _l("Accessible"), sortable: false, thStyle: { width: "10%" } };
        const NAME_FIELD = { key: "name", label: _l("Name"), sortable: true };
        const TYPE_FIELD = { key: "type", label: _l("Type"), sortable: true, thStyle: { width: "10%" } };
        const tableFields = [SHARING_FIELD, TYPE_FIELD, NAME_FIELD];
        watch(props, () => {
            updateReferences(referencedObjects, props.markdownContent);
            initWorkflowData();
            initHistoryDatasetData();
        }, { immediate: true });
        watch(historyIds, () => __awaiter(this, void 0, void 0, function* () {
            for (const historyId of historyIds.value) {
                loadHistoryById(historyId);
                if (historyId && !(historyId in historyAccessible.value)) {
                    Vue.set(historyAccessible.value, historyId, null);
                    const { data, error } = yield GalaxyApi().GET("/api/histories/{history_id}/sharing", {
                        params: { path: { history_id: historyId } },
                    });
                    if (error) {
                        const errorMessage = errorMessageAsString(error);
                        const title = "Failed to fetch history metadata.";
                        toast.error(errorMessage, title);
                        Vue.set(historyAccessible.value, historyId, `${title} Reason: ${errorMessage}.`);
                        return;
                    }
                    const accessible = data.importable;
                    Vue.set(historyAccessible.value, historyId, accessible);
                }
            }
        }));
        function initWorkflowData() {
            return __awaiter(this, void 0, void 0, function* () {
                for (const workflowId of referencedWorkflowIds.value) {
                    fetchWorkflowForInstanceId(workflowId);
                    if (workflowId && !(workflowId in workflowAccessible.value)) {
                        Vue.set(workflowAccessible.value, workflowId, null);
                        const { data, error } = yield GalaxyApi().GET("/api/workflows/{workflow_id}/sharing", {
                            params: { path: { workflow_id: workflowId } },
                        });
                        if (error) {
                            const errorMessage = errorMessageAsString(error);
                            const title = "Failed to fetch workflow metadata.";
                            toast.error(errorMessage, title);
                            Vue.set(workflowAccessible.value, workflowId, `${title} Reason: ${errorMessage}.`);
                            return;
                        }
                        const accessible = data.importable;
                        Vue.set(workflowAccessible.value, workflowId, accessible);
                    }
                }
            });
        }
        function initHistoryDatasetData() {
            for (const historyDatasetId of referencedHistoryDatasetIds.value) {
                fetchDataset({ id: historyDatasetId });
                if (historyDatasetId && !(historyDatasetId in historyDatasetAccessible.value)) {
                    axios
                        .get(withPrefix(`/dataset/get_edit?dataset_id=${historyDatasetId}`))
                        .then((response) => {
                        const permissionDisable = response.data.permission_disable;
                        const permissionInputs = response.data.permission_inputs;
                        if (permissionDisable) {
                            const errorStr = `Cannot modify permissions of this dataset. Reason: ${permissionInputs[0].label}`;
                            Vue.set(historyDatasetAccessible.value, historyDatasetId, errorStr);
                            return;
                        }
                        const accessPermissionInput = permissionInputs[1];
                        if (accessPermissionInput.name != "DATASET_ACCESS") {
                            throw Error("Galaxy Bug");
                        }
                        const accessible = (accessPermissionInput.value || []).length == 0;
                        Vue.set(historyDatasetAccessible.value, historyDatasetId, accessible);
                    })
                        .catch((e) => {
                        const errorMessage = errorMessageAsString(e);
                        const title = "Failed to fetch dataset metadata.";
                        toast.error(errorMessage, title);
                        Vue.set(historyDatasetAccessible.value, historyDatasetId, `${title} Reason: ${errorMessage}.`);
                    });
                }
            }
        }
        const tableItems = computed(() => {
            return [...histories.value, ...workflows.value, ...datasets.value];
        });
        function makeAccessible(item) {
            return __awaiter(this, void 0, void 0, function* () {
                let accessibleResult = undefined;
                let errorResult = undefined;
                let accessibleMap;
                if (item.type == "history") {
                    const { data, error } = yield GalaxyApi().PUT("/api/histories/{history_id}/enable_link_access", {
                        params: { path: { history_id: item.id } },
                    });
                    errorResult = error;
                    accessibleResult = data === null || data === void 0 ? void 0 : data.importable;
                    accessibleMap = historyAccessible;
                }
                else if (item.type == "workflow") {
                    const { data, error } = yield GalaxyApi().PUT("/api/workflows/{workflow_id}/enable_link_access", {
                        params: { path: { workflow_id: item.id } },
                    });
                    errorResult = error;
                    accessibleResult = data === null || data === void 0 ? void 0 : data.importable;
                    accessibleMap = workflowAccessible;
                }
                else if (item.type == "historyDataset") {
                    const { data, error } = yield GalaxyApi().PUT("/api/datasets/{dataset_id}/permissions", {
                        params: { path: { dataset_id: item.id } },
                        body: {
                            action: "remove_restrictions",
                        },
                    });
                    errorResult = error;
                    accessibleResult = data !== undefined;
                    accessibleMap = historyDatasetAccessible;
                }
                else {
                    console.log("Serious client programming error - unknown object type encountered.");
                    return;
                }
                if (errorResult) {
                    const errorMessage = errorMessageAsString(errorResult);
                    const title = "Failed update object accessibility.";
                    toast.error(errorMessage, title);
                    Vue.set(accessibleMap.value, item.id, `${title} Reason: ${errorMessage}.`);
                    return;
                }
                Vue.set(accessibleMap.value, item.id, accessibleResult);
            });
        }
        return { __sfc: true, getHistoryNameById, loadHistoryById, getStoredWorkflowNameByInstanceId, fetchWorkflowForInstanceId, getDataset, fetchDataset, toast, props, referencedObjects, referencedJobIds, referencedHistoryDatasetIds, referencedHistoryDatasetCollectionIds, referencedWorkflowIds, referencedInvocationIds, jobsToHistories, invocationsToHistories, historyDatasetCollectionsToHistories, historyIds, historyAccessible, workflowAccessible, historyDatasetAccessible, catchErrorToToast, histories, workflows, datasets, loading, SHARING_FIELD, NAME_FIELD, TYPE_FIELD, tableFields, initWorkflowData, initHistoryDatasetData, tableItems, makeAccessible, PermissionObjectType, SharingIndicator, LoadingSpan };
    }
});
